/**
 * All that the user interacts with.
 * @module UserInterface
 * @category Display Components
 */

import { lightTheme, measures, strings } from "@quickpoint.me/shared";
import styled, { ThemeProvider } from "styled-components";

import { TourProvider } from "@reactour/tour";
import Auth from "./auth/Auth";
import Editor from "./editor/Editor";
import FileDrop from "../functionalComponents/FileDrop";
import FileUploader from "@quickpoint.me/fileuploader";
import FrameSize from "../functionalComponents/FrameSize";
import { HostId } from "../state/atoms";
import { LocaleAtom } from "../state/localeAtom";
import LogSuspense from "./LogSuspense";
import { NavigationControlBarPortrait } from "./NavigationControlBarPortrait";
import { Authorization, NetworkError } from "../state/authAtoms";
import RefreshSpinner from "./Refresh";
import Share from "./Share";
import { ShowFileUploader, ShowTour } from "../state/uiAtoms";
import Stacks from "./stacks/Stacks";
import { Suspense } from "react";
import TknsPanel from "./TknsPanel";
import TopBar from "./TopBar";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { getTourSteps, qpTourStyles } from "../lib/tour/tour";
import { TourStateBridge } from "../lib/tour/TourStateBridge";
import { TourAnalyticsBridge } from "../lib/tour/TourAnalyticsBridge";
import { QpTourContext } from "../lib/tour/wrapper";

const StyledApp = styled.div`
  overflow: hidden;
  background-color: ${(props) => props.theme.backgroundColor};
  height: ${measures.styledApp.height};
`;

const StyledPortraitGrid = styled.div`
  display: grid;
  height: 100%;
  /* auto top row: collapses to zero when TopBar renders nothing in portrait
     (demo sign-in is the only occupant) — the editor gets the height */
  grid-template-rows: auto 1fr ${measures.navigationControlBar.portrait.height};
  grid-template-areas:
  "top-bar"
  "editor"
  "navigation-portrait";
`;

function UserInterfacePortrait() {
	const hostId = useRecoilValue(HostId);
	const networkError = useRecoilValue(NetworkError);
	const showFileUploader = useRecoilValue(ShowFileUploader);
	const auth = useRecoilValue(Authorization);
	const locale = useRecoilValue(LocaleAtom);
	const setShowTour = useSetRecoilState(ShowTour);
	const isInPortrait = true;

	const closeTour: React.ComponentProps<typeof TourProvider>["onClickClose"] =
		({ setCurrentStep }) => {
			setCurrentStep(0);
			setShowTour(false);
		};

	if (networkError) {
		return (
			<StyledApp>
				<p>There is a problem with the internet. Try refreshing?</p>
			</StyledApp>
		);
	}

	return (
		<ThemeProvider theme={lightTheme}>
			<QpTourContext.Provider value={{ isAuthenticated: auth.isLoggedIn }}>
				{/* portrait always runs the mobile tour — the desktop variants
				    anchor to panes that don't exist here */}
				<TourProvider
					key={`mobile-${locale}`}
					onClickHighlighted={(e) => {
						e.stopPropagation();
					}}
					disableInteraction
					showDots={false}
					disableKeyboardNavigation={false}
					steps={getTourSteps(hostId, "mobile")}
					className="qp-tour"
					styles={qpTourStyles}
					onClickClose={closeTour}
					onClickMask={() => null}
				>
					<TourStateBridge />
					<TourAnalyticsBridge variant="mobile" />
					<Suspense
						fallback={
							<RefreshSpinner fullScreen showBackGround={true} area="Auth" />
						}
					>
						<Auth />
					</Suspense>
					<Suspense fallback={<LogSuspense />}>
						<Stacks />
					</Suspense>
					<Share />
					<TknsPanel />
					<StyledApp
						data-testid="userInterface mobile"
						className={`${strings.css.authorUserInterface} ${strings.css.fileDropZone}`}
					>
						<FrameSize />
						<Suspense fallback={<LogSuspense />}>
							<FileDrop />
						</Suspense>
						<FileUploader
							show={showFileUploader}
							isInPortrait={isInPortrait}
							hostId={hostId}
						/>
						<Suspense fallback={<LogSuspense />}>
							<StyledPortraitGrid className="portrait-grid">
								<TopBar />
								<Suspense fallback={<RefreshSpinner showBackGround={true} />}>
									<Editor />
								</Suspense>
								<NavigationControlBarPortrait />
							</StyledPortraitGrid>
						</Suspense>
					</StyledApp>
				</TourProvider>
			</QpTourContext.Provider>
		</ThemeProvider>
	);
}

export { UserInterfacePortrait };
